import React, { useRef, useEffect, useState, useCallback } from 'react';
import './CameraPreview.css';

/**
 * Component Camera Preview với beauty filter real-time
 * Cho phép điều chỉnh cường độ làm đẹp ngay trong camera preview
 */
function CameraPreview({ 
  onCapture, 
  onClose, 
  initialBeautyIntensity = 50,
  applyBeauty = true 
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const blurCanvasRef = useRef(null); // Reuse blur canvas để tránh memory leak
  const [beautyIntensity, setBeautyIntensity] = useState(initialBeautyIntensity);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' (sau) hoặc 'user' (trước)
  const animationFrameRef = useRef(null);
  const isRenderingRef = useRef(false); // Flag để tránh multiple render calls
  const lastRenderTimeRef = useRef(0); // Throttle render để tránh quá tải

  // Áp dụng beauty filter lên video frame (tối ưu hóa)
  const applyBeautyFilter = useCallback((video, canvas, intensity) => {
    if (!applyBeauty || intensity === 0) {
      return;
    }

    const ctx = canvas.getContext('2d');
    const width = video.videoWidth;
    const height = video.videoHeight;
    
    // Chỉ set size một lần khi thay đổi
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    // Tính toán các tham số dựa trên intensity
    const intensityFactor = intensity / 100;
    const brightness = 1.0 + (0.2 * intensityFactor);
    const contrast = 1.0 + (0.1 * intensityFactor);
    const saturation = 1.0 + (0.1 * intensityFactor);
    const blurLevel = Math.round(2 + (4 * intensityFactor));

    // Vẽ video frame lên canvas với filter
    ctx.filter = `brightness(${brightness.toFixed(2)}) contrast(${contrast.toFixed(2)}) saturate(${saturation.toFixed(2)})`;
    ctx.drawImage(video, 0, 0, width, height);

    // Áp dụng blur overlay cho smooth skin (reuse canvas)
    if (blurLevel > 0) {
      // Tạo blur canvas một lần và reuse
      if (!blurCanvasRef.current) {
        blurCanvasRef.current = document.createElement('canvas');
      }
      const blurCanvas = blurCanvasRef.current;
      
      if (blurCanvas.width !== width || blurCanvas.height !== height) {
        blurCanvas.width = width;
        blurCanvas.height = height;
      }
      
      const blurCtx = blurCanvas.getContext('2d');
      blurCtx.filter = `blur(${blurLevel}px)`;
      blurCtx.drawImage(canvas, 0, 0);

      // Overlay blur với alpha
      const smoothnessAlpha = 0.25 + (0.2 * intensityFactor);
      ctx.globalAlpha = smoothnessAlpha;
      ctx.drawImage(blurCanvas, 0, 0);
      ctx.globalAlpha = 1.0;
    }
  }, [applyBeauty]);

  // Render video với beauty filter real-time (tối ưu hóa)
  const renderFrame = useCallback(() => {
    // Tránh multiple render calls đồng thời
    if (isRenderingRef.current || !videoRef.current || !canvasRef.current || !isStreaming) {
      animationFrameRef.current = requestAnimationFrame(renderFrame);
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Kiểm tra video đã sẵn sàng
    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationFrameRef.current = requestAnimationFrame(renderFrame);
      return;
    }

    // Throttle render: chỉ render mỗi 33ms (30fps) để tránh quá tải
    const now = Date.now();
    const timeSinceLastRender = now - lastRenderTimeRef.current;
    if (timeSinceLastRender < 33) {
      animationFrameRef.current = requestAnimationFrame(renderFrame);
      return;
    }

    isRenderingRef.current = true;
    lastRenderTimeRef.current = now;

    try {
      if (applyBeauty && beautyIntensity > 0) {
        applyBeautyFilter(video, canvas, beautyIntensity);
      } else {
        const ctx = canvas.getContext('2d');
        const width = video.videoWidth;
        const height = video.videoHeight;
        
        // Chỉ set size khi thay đổi
        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width;
          canvas.height = height;
        }
        ctx.drawImage(video, 0, 0);
      }
    } catch (error) {
      console.error('Error rendering frame:', error);
    } finally {
      isRenderingRef.current = false;
      animationFrameRef.current = requestAnimationFrame(renderFrame);
    }
  }, [isStreaming, applyBeauty, beautyIntensity, applyBeautyFilter]);

  // Hàm để stop camera stream hiện tại
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
    isRenderingRef.current = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Hàm để start camera stream
  const startCamera = useCallback(async (facing = facingMode) => {
    try {
      // Stop camera cũ trước
      stopCamera();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facing, // 'environment' (sau) hoặc 'user' (trước)
          width: { ideal: 1280 },
          height: { ideal: 1280 }
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        videoRef.current.onloadedmetadata = () => {
          setIsStreaming(true);
          // renderFrame sẽ được gọi tự động bởi useEffect
        };
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Không thể truy cập camera. Vui lòng kiểm tra quyền truy cập.');
    }
  }, [facingMode, stopCamera]);

  // Hàm để switch camera
  const switchCamera = useCallback(() => {
    const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacingMode);
  }, [facingMode]);

  // Khởi động camera stream
  useEffect(() => {
    startCamera(facingMode);

    return () => {
      // Cleanup
      stopCamera();
      // Cleanup blur canvas
      if (blurCanvasRef.current) {
        blurCanvasRef.current = null;
      }
    };
  }, [facingMode, startCamera, stopCamera]); // Re-run khi facingMode thay đổi

  // Khởi động render loop khi streaming bắt đầu
  useEffect(() => {
    if (isStreaming && videoRef.current && canvasRef.current) {
      // Chỉ start render loop một lần
      if (!animationFrameRef.current) {
        renderFrame();
      }
    } else {
      // Stop render loop khi không streaming
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  }, [isStreaming, renderFrame]);

  // Capture ảnh từ video stream
  const handleCapture = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');

    // Vẽ frame hiện tại lên canvas
    if (applyBeauty && beautyIntensity > 0) {
      // Sử dụng canvas đã có filter
      applyBeautyFilter(video, canvas, beautyIntensity);
    } else {
      const width = video.videoWidth;
      const height = video.videoHeight;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      ctx.drawImage(video, 0, 0);
    }

    // Convert sang blob và tạo File
    canvas.toBlob((blob) => {
      if (blob && onCapture) {
        const file = new File([blob], `camera-${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        onCapture(file);
      }
    }, 'image/jpeg', 0.9);
  }, [videoRef, canvasRef, applyBeauty, beautyIntensity, applyBeautyFilter, onCapture]);

  return (
    <div className="camera-preview-overlay">
      <div className="camera-preview-container">
        <div className="camera-preview-header">
          <h3>Camera với làm đẹp ✨</h3>
          <button className="camera-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && (
          <div className="camera-error">
            {error}
          </div>
        )}

        <div className="camera-preview-wrapper">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-video"
            style={{ display: applyBeauty && beautyIntensity > 0 ? 'none' : 'block' }}
          />
          <canvas
            ref={canvasRef}
            className="camera-canvas"
            style={{ display: applyBeauty && beautyIntensity > 0 ? 'block' : 'none' }}
          />
        </div>

        <div className="camera-controls">
          {applyBeauty && (
            <div className="camera-beauty-controls">
              <label className="camera-beauty-label">
                <span>Cường độ làm đẹp: {beautyIntensity}%</span>
              </label>
              <div className="camera-slider-wrapper">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={beautyIntensity}
                  onChange={(e) => setBeautyIntensity(parseInt(e.target.value))}
                  className="camera-beauty-slider"
                />
                <div className="camera-slider-labels">
                  <span>Nhẹ</span>
                  <span>Vừa</span>
                  <span>Mạnh</span>
                </div>
              </div>
            </div>
          )}

          <div className="camera-action-buttons">
            <button
              className="camera-switch-btn"
              onClick={switchCamera}
              disabled={!isStreaming}
              title={facingMode === 'environment' ? 'Chuyển sang camera trước' : 'Chuyển sang camera sau'}
            >
              🔄 {facingMode === 'environment' ? 'Camera trước' : 'Camera sau'}
            </button>
            <button
              className="camera-capture-btn"
              onClick={handleCapture}
              disabled={!isStreaming}
            >
              📷 Chụp ảnh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CameraPreview;
